import { FC } from 'react';
import {
    CoverageKey,
    IAgencyAlert,
    IAgencyDigest,
    IClientBaseline,
    IHistoryProgress,
    RECENT_WEEKS,
    IClientWellbeing,
    FULL_SCORE,
    WELLBEING_EXPLAINER,
    describeAttention,
    describeBaselines,
    describeCaseload,
    describeChanges,
    describeCoverage,
    describeHistoryRule
} from '@innerhive-internal/innerhive-common';
import { HoneycombCaseloadMap, IHoneycombCell } from './HoneycombCaseloadMap';
import { BaselineDeviationStrip, DEVIATION_EXPLAINER } from './BaselineDeviationStrip';
import { AttentionList } from './AttentionList';
import { WellbeingRow } from './WellbeingRow';
import { CoverageMeters } from './CoverageMeters';
import { InsightsCard } from './InsightsCard';
import { InsightsInfoTip } from './InsightsInfoTip';
import { BeaSays } from './BeaSays';
import { NeedsHistory } from './NeedsHistory';

/**
 * One cell per client, in the caseload's own order rather than worst-first.
 *
 * The honeycomb is the one surface where a client keeps the same position from visit to visit, which
 * is what lets someone find a person on it. Re-sorting it by severity would move everybody every
 * time anything changed; the row of diagrams underneath is the ranked view.
 */
const buildCells = (params: { digest: IAgencyDigest; wellbeing: IClientWellbeing[] }): IHoneycombCell[] => {
    const { digest } = params;
    const wellbeingById = new Map(params.wellbeing.map(one => [one.careMapId, one]));

    // Tallied once rather than filtered per client, which would walk the whole alert list for every
    // cell on the map.
    const incidents = new Map<string, number>();
    for (const alert of digest.alerts) {
        if (alert.kind === 'incident') {
            incidents.set(alert.careMapId, (incidents.get(alert.careMapId) ?? 0) + 1);
        }
    }

    // An incident outranking a client's condition is decided in the digest, not here, or this map would
    // be the only surface applying it and the strip beside it would still draw the client green.
    //
    // The badge counts every incident in the window while the standing counts only a recent one, so
    // a settled cell CAN carry a badge: a fall two months ago is history, and holding somebody at
    // declining for the rest of the window is how a worklist stops being read. The badge is the
    // record that it happened; the colour is whether to go today.
    return digest.baselines.map(baseline => ({
        careMapId: baseline.careMapId,
        clientName: baseline.clientName,
        standing: baseline.standing,
        recordedRecently: baseline.volume.recordedRecently,
        // Named only in the hover text of a cell with a standing. An unknown cell says why it
        // cannot be compared instead, and the modal behind it is unscored to match.
        score: wellbeingById.get(baseline.careMapId)?.score ?? FULL_SCORE,
        incidentCount: incidents.get(baseline.careMapId) ?? 0
    }));
};

/**
 * The dispatcher's half of the tab: where to look, and who to see today.
 *
 * Every surface here is drawn from the one digest it is handed, so the honeycomb, the strip and the
 * list can never disagree about a client. That is why an incident outranking a client's condition is
 * decided in the digest rather than in the cell builder: applied here it would colour the map coral
 * while the strip beside it still drew the same client green. The strip is the one deliberate
 * difference, along with the sentence rendered beneath it, and for the opposite reason: both are
 * about the condition, so they leave out a client flagged for a fall when nothing else about them moved.
 *
 * All three open the same detail view, because the question a reader has after spotting someone
 * is always the same one: what actually happened.
 */
export const InsightsFocusLens: FC<{
    digest: IAgencyDigest;
    wellbeing: IClientWellbeing[];
    history: IHistoryProgress;
    /** The rows the Recent Changes list shows. */
    alerts: IAgencyAlert[];
    /** Everything in the window, so the line under the list can count what the list left out. */
    allAlerts: IAgencyAlert[];
    unlistedAlertCount: number;
    baselines: IClientBaseline[];
    matchingIds?: string[];
    narrowed: boolean;
    openMeter?: CoverageKey;
    onToggleMeter: (key: CoverageKey) => void;
    onOpenClient: (careMapId: string) => void;
}> = ({
    digest, wellbeing, history, alerts, allAlerts, unlistedAlertCount, baselines, matchingIds, narrowed,
    openMeter, onToggleMeter, onOpenClient
}) => {
    return (
        <>
            <InsightsCard
                heading="Caseload Summary"
                action={(
                    <InsightsInfoTip label="About the caseload summary" summary="One hexagon per client, highlighted by how they compare to their own baseline.">
                        {!history.hasEnough && describeHistoryRule(history)}
                    </InsightsInfoTip>
                )}
            >
                <NeedsHistory history={history} panel="caseload">
                    <HoneycombCaseloadMap
                        cells={buildCells({ digest, wellbeing })}
                        matchingIds={matchingIds}
                        onSelectClient={onOpenClient}
                    />

                    <BeaSays panel="caseload">{describeCaseload(digest)}</BeaSays>
                </NeedsHistory>
            </InsightsCard>

            <InsightsCard
                heading="Need Attention"
                action={(
                    <InsightsInfoTip label="About who needs attention" summary="Clients whose recent weeks look the least like their typical baseline.">
                        {history.hasEnough ? WELLBEING_EXPLAINER : describeHistoryRule(history)}
                    </InsightsInfoTip>
                )}
            >
                <NeedsHistory history={history} panel="attention">
                    <WellbeingRow wellbeing={wellbeing} onOpenClient={onOpenClient} />

                    <BeaSays panel="attention">{describeAttention(digest)}</BeaSays>
                </NeedsHistory>
            </InsightsCard>

            <InsightsCard
                heading="Baseline Trends"
                action={(
                    <InsightsInfoTip
                        label="What the figure means"
                        summary={`Observational trends across the last ${RECENT_WEEKS} weeks compared to the same client's earlier baseline.`}
                    >
                        {history.hasEnough ? DEVIATION_EXPLAINER : describeHistoryRule(history)}
                    </InsightsInfoTip>
                )}
            >
                <NeedsHistory history={history} panel="baselines">
                    <BaselineDeviationStrip
                        unreadableCount={digest.unreadableCount}
                        baselines={baselines}
                        narrowed={narrowed}
                        onSelectClient={onOpenClient}
                    />

                    <BeaSays panel="baselines">{describeBaselines({ baselines, narrowed, unreadableCount: digest.unreadableCount })}</BeaSays>
                </NeedsHistory>
            </InsightsCard>

            <InsightsCard
                heading="Recent Changes"
                action={<InsightsInfoTip label="About recent changes" summary="Incidents and baseline changes, most recent first." />}
            >
                <AttentionList
                    alerts={alerts}
                    unlistedCount={unlistedAlertCount}
                    narrowed={narrowed}
                    onSelectClient={onOpenClient}
                />

                <BeaSays panel="changes">{describeChanges({ all: allAlerts, shownCount: alerts.length, unreadableCount: digest.unreadableCount })}</BeaSays>
            </InsightsCard>

            {/* Last on the tab because it is the only panel that is not about this week. What is on
                file is a standing worklist rather than something that changed, so it sits under the
                things a dispatcher opens the tab for. */}
            <InsightsCard
                heading="Documentation Coverage"
                action={<InsightsInfoTip label="What coverage counts" summary="On file documentation for each client. Expand a section to see which clients have gaps in coverage." />}
            >
                <CoverageMeters digest={digest} openMeter={openMeter} onToggleMeter={onToggleMeter} />

                <BeaSays panel="coverage">{describeCoverage({ digest })}</BeaSays>
            </InsightsCard>
        </>
    );
};
